import type { PropType } from 'vue';
import type { Nullable } from '../../types';
import type { TDropdownSelectProps } from './TSelectDropdown.types';
import { merge } from 'es-toolkit/compat';
import { computed, defineComponent, onMounted, onUnmounted, ref } from 'vue';
import { useStyles } from './TSelectDropdown.styles';
import { getVisibleElements } from './TSelectDropdown.utils';

export const TSelectDropdown = defineComponent({
  name: 'TSelectDropdown',
  props: {
    open: Boolean as PropType<TDropdownSelectProps['open']>,
    label: String as PropType<TDropdownSelectProps['label']>,
    selectedValue: String as PropType<TDropdownSelectProps['selectedValue']>,
    disabled: Boolean as PropType<TDropdownSelectProps['disabled']>,
    helperText: String as PropType<TDropdownSelectProps['helperText']>,
    placeholder: String as PropType<TDropdownSelectProps['placeholder']>,
    error: Boolean as PropType<TDropdownSelectProps['error']>,
    fullWidth: Boolean as PropType<TDropdownSelectProps['fullWidth']>,
  },
  emits: ['cancel', 'click'],
  setup(_props, { slots, attrs, emit }) {
    const props = computed(() => merge({}, _props, attrs));

    const baseClass = useStyles();
    const classes = computed(() => [
      baseClass.value,
      `${baseClass.value}__menu_container`,
      {
        [`${baseClass.value}--active`]: props.value.open,
        [`${baseClass.value}--error`]: props.value.error,
      },
    ]);
    const containerClasses = computed(() => [
      baseClass.value,
      `${baseClass.value}__container`,
      {
        [`${baseClass.value}--full-width`]: props.value.fullWidth,
      },
    ]);

    const container = ref<Nullable<HTMLDivElement>>(null);
    const handleClickOutside = (event: Event) => {
      if (
        props.value.open
        && container.value
        && !container.value.contains(event.target as Node)
      ) {
        emit('cancel');
      }
    };
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        emit('cancel');
      }
    };
    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
      container.value?.addEventListener('keydown', handleKeydown);
    });
    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
      container.value?.removeEventListener('keydown', handleKeydown);
    });

    const visibleElements = computed(() => getVisibleElements(slots.default?.() || []));

    const handleClick = () => {
      emit('click');
    };

    return () => (
      <div class={containerClasses.value}>
        {slots.label?.()
        || (props.value.label && (
          <label class={`${baseClass.value}__label`}>
            {props.value.label}
          </label>
        ))}
        <div ref={container} role="menu" class={classes.value}>
          <button
            class={`${baseClass.value}__select`}
            onClick={handleClick}
            disabled={props.value.disabled}
            type="button"
          >
            {props.value.selectedValue}
            {props.value.placeholder && !props.value.selectedValue && (
              <option
                value=""
                disabled
                selected
                class={`${baseClass.value}__placeholder`}
              >
                {props.value.placeholder}
              </option>
            )}
          </button>
          <div class={`${baseClass.value}__menu`}>
            {visibleElements.value.map((element, index, array) => (
              <div>
                {element}
                {index < array.length - 1 && (
                  <div class={`${baseClass.value}__divider`} />
                )}
              </div>
            ))}
          </div>
        </div>
        {slots.helperText?.()
        || (props.value.helperText && (
          <p class={`${baseClass.value}__helper-text`}>
            {props.value.helperText}
          </p>
        ))}
      </div>
    );
  },
});
